import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  AlertTriangle,
  Search,
  ChevronDown,
  Send,
} from "lucide-react";
import { DiscrepancyService } from "../../services/discrepancyService";
import FileUpload from "../FileUpload/FileUpload";
import { formatJobRevNumber } from "../../utils/projectFormatting";
import "./SendBoardDiscrepancyModal.css";
import { useTrackModal } from "../../hooks/useTrackModal";

const SendBoardDiscrepancyModal = ({
  isOpen,
  onClose,
  onSave,
  projects,
  clients,
  employees,
  selectedProject = null, // Pre-selected project from parent
  defaultFileFormats = [], // Default file formats from export template filter
}) => {
  useTrackModal("SendBoardDiscrepancyModal", isOpen);
  const [formData, setFormData] = useState({
    discrepancyType: "Grading Clarification", // Default to Grading Clarification
    projectId: null,
    additionalInformation: "",
    markupAttachment: [],
    loomLink: "",
    emailPreview: "",
    // fileFormatsNeeded removed - this field only exists in projects table, not discrepancies
  });

  const [filteredProjects, setFilteredProjects] = useState([]);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Initialize projects when modal opens
  useEffect(() => {
    if (isOpen && projects) {
      // Sort projects by job number (highest to lowest)
      const sortedProjects = [...projects].sort((a, b) => {
        const parseJobNumber = (jobNum) => {
          if (!jobNum) return 0;
          const parts = jobNum.split("-");
          if (parts.length === 2) {
            const year = parseInt(parts[0]) || 0;
            const num = parseInt(parts[1]) || 0;
            return year * 10000 + num;
          }
          return parseInt(jobNum) || 0;
        };

        const numA = parseJobNumber(a.job_number);
        const numB = parseJobNumber(b.job_number);
        return numB - numA; // Descending order
      });

      setFilteredProjects(sortedProjects);

      // Pre-select project if provided - only do this once when modal first opens
      if (selectedProject && !formData.projectId) {
        setFormData(prev => ({
          ...prev,
          projectId: selectedProject.id,
          // fileFormatsNeeded removed - this field only exists in projects table
        }));
        setProjectSearchTerm(formatProjectDisplay(selectedProject));
        setShowProjectDropdown(false);
        loadProjectDetails(selectedProject.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projects, defaultFileFormats]);

  // Filter projects based on search term
  useEffect(() => {
    if (!projectSearchTerm) {
      setFilteredProjects(projects || []);
      return;
    }

    const searchLower = projectSearchTerm.toLowerCase().trim();
    const filtered = (projects || []).filter((project) => {
      const projectName = (project.name || "").toLowerCase();
      const jobNumber = (project.job_number || "").toLowerCase();
      const formattedJobRev = formatJobRevNumber(
        project.job_number,
        project.rev,
      ).toLowerCase();
      const clientName = (project.clients?.name || "").toLowerCase();
      const contactName = (project.contacts?.name || "").toLowerCase();
      const contactEmail = (project.contacts?.email || "").toLowerCase();

      return (
        projectName.includes(searchLower) ||
        jobNumber.includes(searchLower) ||
        formattedJobRev.includes(searchLower) ||
        clientName.includes(searchLower) ||
        contactName.includes(searchLower) ||
        contactEmail.includes(searchLower)
      );
    });

    setFilteredProjects(filtered);
  }, [projectSearchTerm, projects]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
    };

    if (showProjectDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showProjectDropdown]);

  // Load project details when project is selected
  const loadProjectDetails = async (projectId) => {
    try {
      const projectData = await DiscrepancyService.getProjectWithContacts(projectId);
      setProjectDetails(projectData);
    } catch (err) {
      console.error("Error loading project details:", err);
    }
  };

  // Handle project selection
  const handleProjectSelect = (projectId) => {
    const selectedProject = projects.find((p) => p.id === projectId);
    if (selectedProject) {
      setFormData(prev => ({
        ...prev,
        projectId: projectId,
      }));

      loadProjectDetails(projectId);
      setProjectSearchTerm(formatProjectDisplay(selectedProject));
      setShowProjectDropdown(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file uploads
  const handleFilesChange = useCallback((newFiles) => {
    setFormData(prev => ({
      ...prev,
      markupAttachment: newFiles,
    }));
  }, []);

  // Auto-generate email when type or project changes
  useEffect(() => {
    const autoGenerateEmail = async () => {
      if (formData.discrepancyType && formData.projectId && projectDetails) {
        try {
          const template = await DiscrepancyService.getEmailTemplate(formData.discrepancyType);
          
          if (template) {
            // Get contact first name for personalized greeting
            const contactName = projectDetails?.contacts?.name || "there";
            const contactFirstName = contactName.split(' ')[0] || contactName;
            
            // Start with personalized greeting
            let emailContent = `${contactFirstName},\n\n`;
            emailContent += template;

            // Add attachment section if files are attached
            if (formData.markupAttachment && formData.markupAttachment.length > 0) {
              emailContent += "\n\nAttached files:";
              formData.markupAttachment.forEach((file, index) => {
                emailContent += `\n- ${file.name || file.filename || `Attachment ${index + 1}`}`;
              });
            }

            setFormData(prev => ({
              ...prev,
              emailPreview: emailContent,
            }));
          }
        } catch (err) {
          console.error("Failed to auto-generate email:", err);
        }
      }
    };

    autoGenerateEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.discrepancyType, formData.projectId, formData.markupAttachment, projectDetails]);

  // Handle generate email button click
  const handleGenerateEmail = async () => {
    if (formData.discrepancyType && formData.projectId && projectDetails) {
      try {
        const template = await DiscrepancyService.getEmailTemplate(formData.discrepancyType);
        
        if (template) {
          // Get contact first name for personalized greeting
          const contactName = projectDetails?.contacts?.name || "there";
          const contactFirstName = contactName.split(' ')[0] || contactName;
          
          // Start with personalized greeting
          let emailContent = `${contactFirstName},\n\n`;
          emailContent += template;

          // Add attachment section if files are attached
          if (formData.markupAttachment && formData.markupAttachment.length > 0) {
            emailContent += "\n\nAttached files:";
            formData.markupAttachment.forEach((file, index) => {
              emailContent += `\n- ${file.name || file.filename || `Attachment ${index + 1}`}`;
            });
          }

          setFormData(prev => ({
            ...prev,
            emailPreview: emailContent,
          }));
        }
      } catch (err) {
        console.error("Failed to generate email:", err);
        setError("Failed to generate email content");
      }
    }
  };


  // Handle create discrepancy
  const handleCreateDiscrepancy = async () => {
    // Validate required fields
    if (!formData.projectId) {
      setError("Project selection is required");
      return;
    }


    setLoading(true);
    setError(null);

    try {
      console.log("Creating discrepancy with type:", formData.discrepancyType);
      console.log("Project ID:", formData.projectId);
      
      // Create the discrepancy (without sending email)
      const discrepancy = await DiscrepancyService.createDiscrepancy({
        discrepancyName: formData.discrepancyType, // Use type as name
        discrepancyType: formData.discrepancyType,
        discrepancyStatus: "Waiting to Review", // Start as ready to send with export
        projectId: formData.projectId,
        additionalInformation: formData.additionalInformation || "", // Ensure it's not null
        markupAttachment: formData.markupAttachment || [],
        loomLink: formData.loomLink || "",
        // fileFormatsNeeded removed - this field only exists in projects table
      });

      // Call onSave callback with the created discrepancy so parent can auto-check it
      if (onSave) {
        onSave(discrepancy);
      }

      // Close modal
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to create discrepancy");
    } finally {
      setLoading(false);
    }
  };

  // Handle close - Reset form data when modal actually closes
  const handleClose = () => {
    onClose();
  };

  // Reset form data when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form data only when modal is closed
      setFormData({
        discrepancyType: "Grading Clarification",
        projectId: null,
        additionalInformation: "",
        markupAttachment: [],
        loomLink: "",
        emailPreview: "",
        // fileFormatsNeeded removed
      });
      setProjectDetails(null);
      setProjectSearchTerm("");
      setShowProjectDropdown(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Format project display
  const formatProjectDisplay = (project) => {
    if (!project) return "";
    let display = "";
    const jobRevNumber = formatJobRevNumber(project.job_number, project.rev);
    if (jobRevNumber) {
      display = jobRevNumber + " - ";
    }
    display += project.name || "Unnamed Project";
    return display;
  };

  if (!isOpen) return null;

  return (
    <div className="sendboard-discrepancy-modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="sendboard-discrepancy-modal">
        {/* Header */}
        <div className="modal-header">
          <h2>Create Discrepancy</h2>
          <button className="btn-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="error-banner">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Project Information Header */}
        {projectDetails && (
          <div className="project-header-section">
            <div className="project-info-horizontal">
              <div className="project-info-item">
                <span className="project-info-label">PROJECT:</span>
                <span className="project-info-value">
                  {formatJobRevNumber(projectDetails.job_number, projectDetails.rev)} - {projectDetails.name}
                </span>
              </div>
              <div className="project-info-item">
                <span className="project-info-label">CLIENT:</span>
                <span className="project-info-value">
                  {projectDetails.clients?.name || "Unknown Client"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="modal-content">
          <div className="form-fields">
            {/* Project Selection - only show if not pre-selected */}
            {!selectedProject && (
              <div className="form-group form-field-full-width" ref={dropdownRef}>
              <label htmlFor="projectSearch">
                PROJECT <span className="required">*</span>
              </label>
              <div className="project-search-container">
                <div className="project-search-input-wrapper">
                  <Search className="search-icon" size={16} />
                  <input
                    type="text"
                    id="projectSearch"
                    value={projectSearchTerm}
                    onChange={(e) => {
                      setProjectSearchTerm(e.target.value);
                      setShowProjectDropdown(true);
                    }}
                    onFocus={() => setShowProjectDropdown(true)}
                    placeholder="Search by project name, job number, or client..."
                    className="project-search-input"
                    autoComplete="off"
                  />
                  <ChevronDown className="dropdown-icon" size={16} />
                </div>

                {showProjectDropdown && (
                  <div className="project-dropdown">
                    {filteredProjects.length === 0 ? (
                      <div className="project-dropdown-empty">
                        {projectSearchTerm ? "No projects found" : "No projects available"}
                      </div>
                    ) : (
                      <div className="project-dropdown-list">
                        {filteredProjects.map((project) => (
                          <div
                            key={project.id}
                            className={`project-dropdown-item ${formData.projectId === project.id ? "selected" : ""}`}
                            onClick={() => handleProjectSelect(project.id)}
                          >
                            <div className="project-dropdown-item-main">
                              <span className="project-job-number">
                                {formatJobRevNumber(project.job_number, project.rev)}
                              </span>
                              <span className="project-name">
                                {project.name || "Unnamed Project"}
                              </span>
                            </div>
                            {project.clients?.name && (
                              <div className="project-dropdown-item-sub">
                                {project.clients.name}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              </div>
            )}

            {/* Type (changeable) */}
            <div className="form-group">
              <label htmlFor="discrepancyType">
                TYPE <span className="required">*</span>
              </label>
              <select
                id="discrepancyType"
                name="discrepancyType"
                value={formData.discrepancyType}
                onChange={handleChange}
                className="project-search-input"
              >
                {DiscrepancyService.getDiscrepancyTypes().map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <small className="field-hint">Defaults to Grading Clarification</small>
            </div>

            {/* Additional Information */}
            <div className="form-group form-field-full-width">
              <label htmlFor="additionalInformation">
                ADDITIONAL INFORMATION
              </label>
              <textarea
                id="additionalInformation"
                name="additionalInformation"
                value={formData.additionalInformation}
                onChange={handleChange}
                placeholder="Describe the grading clarification needed..."
                className="form-textarea"
                rows="4"
              />
            </div>

            {/* File Attachments */}
            <div className="form-group form-field-full-width">
              <label>FILE ATTACHMENTS</label>
              <FileUpload
                files={formData.markupAttachment}
                onFilesChange={handleFilesChange}
                projectId={formData.projectId}
                clientId={projectDetails?.client_id}
                disabled={loading}
                maxFiles={100}
              />
            </div>

            {/* Loom Link */}
            <div className="form-group form-field-full-width">
              <label htmlFor="loomLink">LOOM LINK</label>
              <input
                type="url"
                id="loomLink"
                name="loomLink"
                value={formData.loomLink}
                onChange={handleChange}
                placeholder="https://www.loom.com/share/..."
                className="project-search-input"
              />
              <small className="field-hint">Link to Loom recording for additional context</small>
            </div>


          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-left">
            {/* Info about creating discrepancy */}
            <small className="send-info">
              Discrepancy will be created and auto-attached to the export.
            </small>
          </div>
          <div className="footer-right">
            <button className="btn-cancel" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button
              className="btn-create-send"
              onClick={handleCreateDiscrepancy}
              disabled={loading || !formData.projectId}
            >
              <Send size={16} />
              {loading ? "Creating..." : "Create Discrepancy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendBoardDiscrepancyModal;